import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Services from './pages/Services';
import Budgets from './pages/Budgets';
import Clients from './pages/Clients';
import Technicians from './pages/Technicians';
import Users from './pages/Users';
import CalendarView from './pages/CalendarView';
import Instructions from './pages/Instructions';
import Reports from './pages/Reports';
import Inventory from './pages/Inventory';
import Stock from './pages/Stock';
import Pendientes from './pages/Pendientes';
import PresupuestosV2 from './pages/PresupuestosV2';
import ControlSheet from './pages/ControlSheet';
import Caja from './pages/Caja';
import Finance from './pages/Finance';
import AgendaTrabajos from './pages/AgendaTrabajos';
import Attendance from './pages/Attendance';
import AttendanceAdmin from './pages/AttendanceAdmin';
import Layout from './components/Layout';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Theme Context
export const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowRestrictedRoles = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Técnicos y ayudantes SOLO pueden acceder a /asistencia
  if (!allowRestrictedRoles && (user.role === 'tecnico' || user.role === 'ayudante')) {
    return <Navigate to="/asistencia" replace />;
  }

  return children;
};

// HomeRedirect: técnico/ayudante → /asistencia; resto → /clients
const HomeRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'tecnico' || user.role === 'ayudante') return <Navigate to="/asistencia" replace />;
  return <Navigate to="/clients" replace />;
};

// AttendanceRoute: técnicos/ayudantes ven Attendance sin Layout; admin/oficina lo ven con Layout + vista admin en segunda pestaña
const AttendanceRoute = () => {
  const { user } = useAuth();
  if (user?.role === 'tecnico' || user?.role === 'ayudante') {
    return <Attendance />;
  }
  return <Layout><AttendanceAdmin /></Layout>;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'light';
  });

  // Global interceptor: auto-logout on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && user) {
          localStorage.removeItem('token');
          localStorage.removeItem('user_id');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [user]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Validate token is not expired before using it
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp * 1000; // Convert to ms
        if (Date.now() >= expiry) {
          // Token expired - clean up completely
          localStorage.removeItem('token');
          localStorage.removeItem('user_id');
          delete axios.defaults.headers.common['Authorization'];
          setLoading(false);
          return;
        }
      } catch {
        // Invalid token format - clean up
        localStorage.removeItem('token');
        localStorage.removeItem('user_id');
        delete axios.defaults.headers.common['Authorization'];
        setLoading(false);
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then(res => {
          // Verify the user from server matches what we expect
          const savedUserId = localStorage.getItem('user_id');
          if (savedUserId && res.data.id !== savedUserId) {
            // Token belongs to a different user - force re-login
            localStorage.removeItem('token');
            localStorage.removeItem('user_id');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
          } else {
            localStorage.setItem('user_id', res.data.id);
            setUser(res.data);
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user_id');
          delete axios.defaults.headers.common['Authorization'];
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user_id', userData.id);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <AuthContext.Provider value={{ user, login, logout, loading }}>
        <div className="App">
          <Toaster richColors position="top-right" />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/asistencia" element={
                <ProtectedRoute allowRestrictedRoles>
                  <AttendanceRoute />
                </ProtectedRoute>
              } />
              <Route path="/services" element={
                <ProtectedRoute>
                  <Layout>
                    <Services />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/budgets" element={
                <ProtectedRoute>
                  <Layout>
                    <Budgets />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/clients" element={
                <ProtectedRoute>
                  <Layout>
                    <Clients />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/technicians" element={
                <ProtectedRoute>
                  <Layout>
                    <Technicians />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/users" element={
                <ProtectedRoute>
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/calendar" element={
                <ProtectedRoute>
                  <Layout>
                    <CalendarView />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/instructions" element={
                <ProtectedRoute>
                  <Layout>
                    <Instructions />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute>
                  <Layout>
                    <Reports />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/inventory" element={
                <ProtectedRoute>
                  <Layout>
                    <Inventory />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/stock" element={
                <ProtectedRoute>
                  <Layout>
                    <Stock />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/control-sheet" element={
                <ProtectedRoute>
                  <Layout>
                    <ControlSheet />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/pendientes" element={
                <ProtectedRoute>
                  <Layout>
                    <Pendientes />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/cotizaciones" element={
                <ProtectedRoute>
                  <Layout>
                    <PresupuestosV2 />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/caja" element={
                <ProtectedRoute>
                  <Layout>
                    <Caja />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/control-financiero" element={
                <ProtectedRoute>
                  <Layout>
                    <Finance />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/agenda" element={
                <ProtectedRoute>
                  <Layout>
                    <AgendaTrabajos />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="*" element={<Navigate to="/clients" replace />} />
            </Routes>
          </BrowserRouter>
        </div>
      </AuthContext.Provider>
    </ThemeContext.Provider>
  );
}

export default App;
